'use client';
import Head from 'next/head';
import { useState, useMemo } from 'react';
import { AngleIcon } from '@/components/icons';
import MethodPageLayout from '@/components/layouts/MethodPageLayout';
import AngleVisualiser from '@/components/methods/AngleVisualiser';
import SliderControl from '@/components/common/SliderControl';
import StepperControl from '@/components/common/StepperControl';
import ColorSwatchRow from '@/components/common/ColorSwatchRow';
import InfoCard from '@/components/common/InfoCard';

const ANGLE_COLORS = ['#534AB7', '#0F6E56', '#854F0B', '#185FA5', '#993C1D', '#f4a261'];

export default function AnglePage() {
    const [angle, setAngle] = useState(45);
    const [strokeColor, setStrokeColor] = useState('#1a1a18');
    const [arcColor, setArcColor] = useState('#993C1D');
    const [strokeWidth, setStrokeWidth] = useState(2);

    const onReset = () => {
        setAngle(45);
        setStrokeColor('#1a1a18');
        setArcColor('#993C1D');
        setStrokeWidth(2);
    };

    const options = useMemo(
        () => ({
            strokeColor,
            arcColor,
            strokeWidth: strokeWidth || 2,
        }),
        [strokeColor, arcColor, strokeWidth],
    );

    const infoRows = useMemo(() => {
        const radians = (angle * (Math.PI / 180)).toFixed(4);
        let quadrant = '1st';
        if (angle > 90 && angle <= 180) quadrant = '2nd';
        else if (angle > 180 && angle <= 270) quadrant = '3rd';
        else if (angle > 270) quadrant = '4th';

        return [
            { key: 'Degrees', value: `${angle}°` },
            { key: 'Radians', value: radians },
            { key: 'Quadrant', value: quadrant },
        ];
    }, [angle]);

    const controls = (
        <>
            <SliderControl
                label="Angle"
                value={angle}
                min={0}
                max={360}
                onChange={setAngle}
                unit="°"
            />
            <StepperControl
                label="Stroke Width"
                value={strokeWidth}
                min={1}
                max={10}
                onChange={setStrokeWidth}
            />
            <ColorSwatchRow
                label="Arc Color"
                colors={ANGLE_COLORS}
                value={arcColor}
                onChange={setArcColor}
            />
            <InfoCard rows={infoRows} />
        </>
    );

    return (
        <>
            <Head>
                <title>Angle Visualiser</title>
            </Head>
            <MethodPageLayout
                icon={<AngleIcon size={20} />}
                iconColor="coral"
                title="Angle visualiser"
                description="Visualise angles from 0 to 360 degrees."
                category="visualiser"
                controls={controls}
                onReset={onReset}
            >
                <AngleVisualiser angle={angle} options={options} />
            </MethodPageLayout>
        </>
    );
}
